import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Plus, Edit2, Trash2, X, ChevronLeft, ChevronRight } from "lucide-react";
import { getApiUrl } from "../../config/api";
import { useAutoDismiss } from "../../hooks/useAutoDismiss";
import "../../styles/DashboardLayout.css";
import "../../styles/UtopiaPages.css";

interface Headteacher {
  id: string;
  name: string;
  title?: string;
  email: string;
  role: string;
  phone?: string;
  nrcNumber?: string;
  qualification?: string;
  accountName?: string;
  bankName?: string;
  bankAccountNumber?: string;
  bankBranch?: string;
  baseSalary?: number;
  housingAllowance?: number;
  transportAllowance?: number;
  responsibilityAllowance?: number;
  otherAllowances?: number;
  createdAt?: string;
}

const Headteachers: React.FC = () => {
  const [headteachers, setHeadteachers] = useState<Headteacher[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingHeadteacher, setEditingHeadteacher] = useState<Headteacher | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    title: "",
    email: "",
    phone: "",
    password: "",
    role: "headteacher",
    nrcNumber: "",
    qualification: "",
    accountName: "",
    bankName: "",
    bankAccountNumber: "",
    bankBranch: "",
    baseSalary: "",
    housingAllowance: "0",
    transportAllowance: "0",
    responsibilityAllowance: "0",
    otherAllowances: "0",
  });
  const [message, setMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  const token = localStorage.getItem("token");
  const role = (localStorage.getItem("role") || "").toLowerCase();
  const isReadOnly = role === "headteacher";

  useAutoDismiss(error, setError, "", 5000);
  useAutoDismiss(message, setMessage, "", 5000);

  const fetchHeadteachers = useCallback(async () => {
    try {
      if (!token) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError("");
      const res = await axios.get(getApiUrl("users/teachers"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const allUsers = Array.isArray(res.data) ? res.data : res.data.teachers || [];
      const filtered = allUsers.filter((u: Headteacher) => (u.role || "").toLowerCase() === "headteacher");
      setHeadteachers(filtered);
    } catch (err) {
      setError("Failed to load headteachers. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchHeadteachers();
  }, [fetchHeadteachers]);

  const filteredHeadteachers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return headteachers;
    return headteachers.filter(
      (h) =>
        h.name.toLowerCase().includes(term) ||
        h.email.toLowerCase().includes(term)
    );
  }, [headteachers, searchTerm]);

  const totalPages = Math.ceil(filteredHeadteachers.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredHeadteachers.slice(indexOfFirstItem, indexOfLastItem);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const openAddModal = () => {
    setEditingHeadteacher(null);
    setFormData({
      name: "",
      title: "",
      email: "",
      phone: "",
      password: "",
      role: "headteacher",
      nrcNumber: "",
      qualification: "",
      accountName: "",
      bankName: "",
      bankAccountNumber: "",
      bankBranch: "",
      baseSalary: "",
      housingAllowance: "0",
      transportAllowance: "0",
      responsibilityAllowance: "0",
      otherAllowances: "0",
    });
    setIsModalOpen(true);
  };

  const openEditModal = (headteacher: Headteacher) => {
    setEditingHeadteacher(headteacher);
    setFormData({
      name: headteacher.name,
      title: headteacher.title || "",
      email: headteacher.email,
      phone: headteacher.phone || "",
      password: "",
      role: headteacher.role || "headteacher",
      nrcNumber: headteacher.nrcNumber || "",
      qualification: headteacher.qualification || "",
      accountName: headteacher.accountName || "",
      bankName: headteacher.bankName || "",
      bankAccountNumber: headteacher.bankAccountNumber || "",
      bankBranch: headteacher.bankBranch || "",
      baseSalary: headteacher.baseSalary?.toString() || "",
      housingAllowance: headteacher.housingAllowance?.toString() || "0",
      transportAllowance: headteacher.transportAllowance?.toString() || "0",
      responsibilityAllowance: headteacher.responsibilityAllowance?.toString() || "0",
      otherAllowances: headteacher.otherAllowances?.toString() || "0",
    });
    setIsModalOpen(true);
  };

  const handleDeleteHeadteacher = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this headteacher?")) return;
    try {
      await axios.delete(getApiUrl(`users/${id}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchHeadteachers();
    } catch (err: any) {
      setMessage(err.response?.data?.message || "Error deleting headteacher");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingHeadteacher) {
        await axios.put(getApiUrl(`users/${editingHeadteacher.id}`), formData, { headers: { Authorization: `Bearer ${token}` } });
        await axios.post(getApiUrl(`payroll/teachers/${editingHeadteacher.id}`), {
          baseSalary: formData.baseSalary,
          housingAllowance: formData.housingAllowance,
          transportAllowance: formData.transportAllowance,
          responsibilityAllowance: formData.responsibilityAllowance,
          otherAllowances: formData.otherAllowances
        }, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(getApiUrl("auth/signup"), formData, { headers: { Authorization: `Bearer ${token}` } });
      }
      fetchHeadteachers();
      setIsModalOpen(false);
    } catch (err: any) {
      setMessage(err.response?.data?.message || "Error saving headteacher");
    }
  };

  const columnCount = isReadOnly ? 3 : 4;

  if (loading && headteachers.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <main className="dashboard-main">
        <div className="utopia-page-container">
          <div className="utopia-table-header">
            <div>
              <h2>Headteachers Directory</h2>
              <p className="page-subtitle">Manage headteacher accounts and access</p>
            </div>
            {!isReadOnly && (
              <button className="utopia-btn-primary flex items-center gap-2" onClick={openAddModal}>
                <Plus size={18} /> Add Headteacher
              </button>
            )}
          </div>

          <div className="mb-4">
            <input
              type="text"
              placeholder="Search headteachers..."
              className="utopia-input w-full"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          {error && (
            <div className="utopia-form-error mb-4">
              {error}
            </div>
          )}

          <table className="utopia-table">
            <thead>
              <tr>
                <th>Headteacher</th>
                <th>Email Address</th>
                <th>Phone</th>
                <th>Role</th>
                {!isReadOnly && <th className="text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {currentItems.map((h) => (
                <tr key={h.id}>
                  <td>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-purple-50 flex items-center justify-center text-purple-600 font-bold text-xs">
                        {h.name.charAt(0)}
                      </div>
                      <span className="font-semibold">{h.name}</span>
                    </div>
                  </td>
                  <td>{h.email}</td>
                  <td>{h.phone || "N/A"}</td>
                  <td><span className="utopia-badge bg-purple-50 text-purple-600">Headteacher</span></td>
                  {!isReadOnly && (
                    <td className="text-right">
                      <div className="flex justify-end gap-2">
                        <button className="utopia-btn-icon" onClick={() => openEditModal(h)}><Edit2 size={16} /></button>
                        <button className="utopia-btn-icon text-red-400 hover:text-red-600" onClick={() => handleDeleteHeadteacher(h.id)}><Trash2 size={16} /></button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {currentItems.length === 0 && (
                <tr>
                  <td colSpan={columnCount} className="text-center text-slate-400 py-8">
                    No headteachers found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="utopia-pagination">
              <button
                className="utopia-page-btn utopia-page-btn-nav"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(p => p - 1)}
                aria-label="Previous page"
              >
                <ChevronLeft size={16} />
              </button>

              {(() => {
                const pages: (number | string)[] = [];
                const maxVisible = 5;

                if (totalPages <= maxVisible) {
                  for (let i = 1; i <= totalPages; i++) pages.push(i);
                } else {
                  pages.push(1);
                  if (currentPage > 3) pages.push('...');
                  const start = Math.max(2, currentPage - 1);
                  const end = Math.min(totalPages - 1, currentPage + 1);
                  for (let i = start; i <= end; i++) {
                    if (i !== 1 && i !== totalPages) pages.push(i);
                  }
                  if (currentPage < totalPages - 2) pages.push('...');
                  pages.push(totalPages);
                }

                return pages.map((p, idx) => {
                  if (p === '...') {
                    return <span key={`ellipsis-${idx}`} className="utopia-pagination-ellipsis">...</span>;
                  }
                  return (
                    <button
                      key={p}
                      className={`utopia-page-btn ${currentPage === p ? 'active' : ''}`}
                      onClick={() => setCurrentPage(p as number)}
                    >
                      {p}
                    </button>
                  );
                });
              })()}

              <button
                className="utopia-page-btn utopia-page-btn-nav"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(p => p + 1)}
                aria-label="Next page"
              >
                <ChevronRight size={16} />
              </button>

              <div className="utopia-pagination-info">
                <span>
                  Showing <strong>{indexOfFirstItem + 1}</strong> - <strong>{Math.min(indexOfLastItem, filteredHeadteachers.length)}</strong> of <strong>{filteredHeadteachers.length}</strong>
                </span>
              </div>
            </div>
          )}
        </div>
      </main>

      {isModalOpen && (
        <div className="utopia-modal-overlay" onClick={(e) => e.target === e.currentTarget && setIsModalOpen(false)}>
          <div className="utopia-modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="utopia-modal-header">
              <h3 className="text-lg font-bold">{editingHeadteacher ? "Edit Headteacher" : "Add Headteacher"}</h3>
              <button
                className="utopia-modal-close-btn"
                onClick={() => setIsModalOpen(false)}
                aria-label="Close modal"
              >
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleSubmit} onKeyDown={(e) => e.key === 'Escape' && setIsModalOpen(false)}>
              {message && (
                <div className={`${message.includes('Error') ? 'utopia-form-error' : 'utopia-form-success'} mb-4`}>
                  {message}
                </div>
              )}
              <div className="utopia-form-group">
                <label className="utopia-label required">Full Name</label>
                <input
                  type="text"
                  name="name"
                  className="utopia-input"
                  placeholder="Enter full name (e.g. John Doe)"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  autoFocus
                />
              </div>
              <div className="utopia-form-group">
                <label className="utopia-label required">Title</label>
                <input
                  type="text"
                  name="title"
                  list="headteacher-title-options"
                  className="utopia-input"
                  placeholder="Mr, Ms, Mrs, Miss..."
                  value={formData.title}
                  onChange={handleChange}
                  required={!editingHeadteacher}
                />
                <datalist id="headteacher-title-options">
                  <option value="Mr" />
                  <option value="Ms" />
                  <option value="Mrs" />
                  <option value="Miss" />
                  <option value="Dr" />
                  <option value="Prof" />
                  <option value="Rev" />
                </datalist>
              </div>
              <div className="utopia-form-group">
                <label className="utopia-label required">Email Address</label>
                <input
                  type="email"
                  name="email"
                  className="utopia-input"
                  placeholder="headteacher@school.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="utopia-form-group">
                <label className="utopia-label required">Phone Number</label>
                <input
                  type="tel"
                  name="phone"
                  className="utopia-input"
                  placeholder="+260971234567"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="utopia-form-group">
                  <label className="utopia-label">NRC Number</label>
                  <input
                    type="text"
                    name="nrcNumber"
                    className="utopia-input"
                    placeholder="NRC (e.g. 123456/11/1)"
                    value={formData.nrcNumber}
                    onChange={handleChange}
                  />
                </div>
                <div className="utopia-form-group">
                  <label className="utopia-label required">Qualification</label>
                  <select
                    name="qualification"
                    className="utopia-input"
                    value={formData.qualification}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select qualification</option>
                    <option value="Certificate">Certificate</option>
                    <option value="Degree">Degree</option>
                    <option value="Diploma">Diploma</option>
                    <option value="ECE">ECE</option>
                    <option value="Grade 12">Grade 12</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="utopia-form-group">
                  <label className="utopia-label required">Account Name</label>
                  <input
                    type="text"
                    name="accountName"
                    className="utopia-input"
                    placeholder="Account Name"
                    value={formData.accountName}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="utopia-form-group">
                  <label className="utopia-label">Bank Name</label>
                  <input
                    type="text"
                    name="bankName"
                    className="utopia-input"
                    placeholder="Bank Name"
                    value={formData.bankName}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="utopia-form-group">
                  <label className="utopia-label required">Branch</label>
                  <input
                    type="text"
                    name="bankBranch"
                    className="utopia-input"
                    placeholder="Branch"
                    value={formData.bankBranch}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="utopia-form-group">
                <label className="utopia-label required">Account Number</label>
                <input
                  type="text"
                  name="bankAccountNumber"
                  className="utopia-input"
                  placeholder="Account Number"
                  value={formData.bankAccountNumber}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="utopia-form-group">
                <label className="utopia-label required">Base Salary</label>
                <input
                  type="number"
                  name="baseSalary"
                  className="utopia-input"
                  placeholder="0.00"
                  value={formData.baseSalary}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="utopia-form-group">
                  <label className="utopia-label">Housing Allowance</label>
                  <input
                    type="number"
                    name="housingAllowance"
                    className="utopia-input"
                    placeholder="0.00"
                    value={formData.housingAllowance}
                    onChange={handleChange}
                  />
                </div>
                <div className="utopia-form-group">
                  <label className="utopia-label">Transport Allowance</label>
                  <input
                    type="number"
                    name="transportAllowance"
                    className="utopia-input"
                    placeholder="0.00"
                    value={formData.transportAllowance}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="utopia-form-group">
                  <label className="utopia-label">Responsibility Allowance</label>
                  <input
                    type="number"
                    name="responsibilityAllowance"
                    className="utopia-input"
                    placeholder="0.00"
                    value={formData.responsibilityAllowance}
                    onChange={handleChange}
                  />
                </div>
                <div className="utopia-form-group">
                  <label className="utopia-label">Other Allowances</label>
                  <input
                    type="number"
                    name="otherAllowances"
                    className="utopia-input"
                    placeholder="0.00"
                    value={formData.otherAllowances}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="utopia-form-group">
                <label className={`utopia-label ${!editingHeadteacher ? 'required' : ''}`}>
                  {editingHeadteacher ? "New Password (Optional)" : "Password"}
                </label>
                <input
                  type="password"
                  name="password"
                  className="utopia-input"
                  placeholder="Enter secure password"
                  value={formData.password}
                  onChange={handleChange}
                  required={!editingHeadteacher}
                  minLength={6}
                />
                {editingHeadteacher && (
                  <span className="utopia-label-hint">Leave blank to keep current password</span>
                )}
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  className="utopia-btn-secondary flex-1 py-3"
                  onClick={() => setIsModalOpen(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="utopia-btn-primary flex-1 py-3">
                  {editingHeadteacher ? "Save Changes" : "Register Headteacher"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Headteachers;
